import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface Education {
  degree: string;
  institution: string;
  year: string;
}

interface FreelancerProfile {
  completedJobs: any[];
  _id: string;
  name: string;
  fullName: string;
  userId: string;
  profilePicture: string;
  experience: number;
  phone: string;
  designation: string;
  rating: number;
  reviews: object;
  skills: string[];
  category: string;
  projectsCompleted: number;
  resume_url: string | null;
  certifications: string[];
  achievements: string[];
  dailyRate: number;
  totalEarnings: number;
  bio: string;
  availability: boolean;
  socialMediaLinks: {
    _id: string;
  }[];
  education: Education[];
  project: any;
  location: string;
  createdAt: string;
  updatedAt: string;
}

interface applicationDetails {
  _id: string;
  jobId: {
    _id: string;
    userId: string;
    profilePicture: string;
    enterpriseId: string;
    title: string;
    description: string;
    category: string;
    requiredSkills: string[];
    budget: number;
    PricingDetails: string;
    duration: string;
    otherRequirements: string[];
    projectvisibility: boolean;
    createdAt: string;
    updatedAt: string;
  };
  freelancerId: string | null;
  userId: string;
  coverLetterText: string;
  coverLetterFileUrl: string;
  proposedBudget: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface AllFreelancerProps {
  search: string;
  skills: string;
  category: string;
  dailyRate: string;
  rating: number | string;
  availability?: string | boolean;
  sortBy: string;
  order: string;
  page: number;
  limit: number;
}

interface FreelancerState {
  profile: FreelancerProfile | null;
  freelancerList: any | null;
  progress: number;
  appDetails: applicationDetails | null;
  isLoading: boolean;
  error: string | null;
  fetchFreelancerProfile: (id: string, token: string, freelancer?: FreelancerProfile | null ) => Promise<void>;
  fetchAllFreelancer: (data: AllFreelancerProps,token: string) => Promise<void>;
  createFreelancerProfile: (
    payload: Partial<FreelancerProfile>,
    token: string
  ) => Promise<any>;
  updateFreelancerProfile: (
    id: string,
    data: Partial<FreelancerProfile>,
    token: string
  ) => Promise<void>;
  getFreelancerJobApplicationDetails: (
    freelancerId: string,
    applicationId: string,
    token: string
  ) => Promise<void>;
  uploadFreelancerResume: (
    userId: string,
    role: string,
    token: string,
    formData: FormData
  ) => Promise<any>;
  uploadFreelancerProfilePicture: (
    file: File,
    token: string,
    userId: string,
    uploadType?: string ,
  ) => Promise<any>;
  clearFreelancerProfile: () => void;
  setPartialProfile: (data: Partial<FreelancerProfile>) => void;
}

export const useFreelancerStore = create<FreelancerState>()(
  persist(
    (set) => ({
      profile: null,
      freelancerList: [],
      isLoading: false,
      error: null,
      appDetails: null,
      progress: 0,

      fetchFreelancerProfile: async (id, token, freelancer) => {
        if (freelancer) {
          set({ profile: freelancer, isLoading: false });
        } else {
          set({ isLoading: true, error: null });
          try {
            const res = await Axios.get(
              `${API_END_POINTS.GET_FREELANCER_PROFILE}/${id}`,
              authHeaders(token)
            );
            set({ profile: res.data, isLoading: false });
          } catch (err: any) {
            set({ error: err.message, isLoading: false });
          }
        }
      },

      fetchAllFreelancer: async (data,token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_ALL_FREELANCER}?search=${data.search}&skills=${data.skills}&category=${data.category}&dailyRate=${data.dailyRate}&rating=${data.rating}&availability=${data.availability}&sortBy=${data.sortBy || "createdAt"}&order=${data.order || "desc"}&page=${data.page || 1}&limit=${data.limit || 10}`,
            authHeaders(token)
          );
          set({ freelancerList: res.data, isLoading: false });
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
        }
      },

      createFreelancerProfile: async (payload, token) => {
        set({ isLoading: true, error: null });

        try {
          const res = await Axios.post(
            `${API_END_POINTS.CREATE_FREELANCER_PROFILE}`,
            payload,
            authHeaders(token)
          );
          set({ profile: res.data, isLoading: false });
          return res;
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
        }
      },

      updateFreelancerProfile: async (id, updatedData, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.put(
            `${API_END_POINTS.UPDATE_FREELANCER_PROFILE}/${id}`,
            updatedData,
            authHeaders(token)
          );
          //   if (!res.ok) throw new Error('Failed to update profile');
          //   const data = await res.json();
          set({ profile: res.data, isLoading: false });
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
        }
      },

      getFreelancerJobApplicationDetails: async (
        freelancerId,
        applicationId,
        token
      ) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_FREELANCER_APPLICATIONS}/${freelancerId}/${applicationId}`,
            authHeaders(token)
          );
          set({ appDetails: res.data, isLoading: false });
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
        }
      },

      uploadFreelancerResume: async (userId, role, token, formData) => {
        set({ isLoading: true, error: null, progress: 0 }); // Reset progress
        try {
          const res = await Axios.post(
            `${API_END_POINTS.UPLOAD_FREELANCER_RESUME}?userId=${userId}&uploadType=resume&role=${role}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data", // Ensure correct content type
              },
              onUploadProgress: (progressEvent) => {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / (progressEvent.total || 1)
                );
                set({ progress: percent }); // Update progress state
              },
            }
          );
          set({ profile: res.data, isLoading: false, progress: 100 }); // Update profile with response data
          return res;
        } catch (err: any) {
          set({ error: err.message, isLoading: false, progress: 0 });
        }
      },
      uploadFreelancerProfilePicture: async (
        file: File,
        token: string,
        userId: string,
        uploadType: string = 'profilePicture',
      ): Promise<any> => {
        set({ isLoading: true, error: null });

        const formData = new FormData();
        formData.append('profilePicture', file);
        formData.append('userId', userId);
        formData.append('uploadType', uploadType);

        let url = `${API_END_POINTS.UPLOAD_FREELANCER_PROFILE_PICTURE}?userId=${encodeURIComponent(userId)}&uploadType=${encodeURIComponent(uploadType)}&role=freelancer`;

        try {
          const res = await Axios.post(
            url,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          set((state) => ({
            profile: {
              ...state.profile,
              profilePicture: res.data.profilePicture,
            },
            isLoading: false,
          }));
          return res;
        } catch (err: any) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      },

      setPartialProfile: (data) =>
        set((state) => ({
          profile: state.profile
            ? { ...state.profile, ...data }
            : ({ ...data } as FreelancerProfile),
        })),

      clearFreelancerProfile: () =>
        set({ profile: null, isLoading: false, error: null }),
    }),
    {
      name: "freelancer-storage",
      partialize: (state) => ({
        profile: state.profile,
        error: state.error,
      }),
      merge: (persisted: Partial<FreelancerState>, initial) => ({
        ...initial,
        profile: persisted?.profile || initial.profile,
        error: persisted?.error || initial.error,
      }),
    }
  )
);
